// StepVisibility - Sichtbarkeits-Auswahl (Schritt 5)
// User waehlt zwischen privat und oeffentlich

import React from 'react';
import { Lock, Globe, Eye, Users, Shield, Clock } from 'lucide-react';
import type { PlaceVisibility } from '../../../types';

interface FormData {
  visibility: PlaceVisibility;
  name?: string;
}

interface StepVisibilityProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

export function StepVisibility({ formData, updateFormData }: StepVisibilityProps) {
  const visibility = formData.visibility || 'public';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Wer soll deinen Ort sehen?
        </h2>
        <p className="text-gray-600">
          Waehle aus, ob dein GoodPlace privat oder oeffentlich sein soll.
        </p>
      </div>

      {/* Preview */}
      {formData.name && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500">Du erstellst:</p>
          <p className="font-medium text-gray-900 text-lg">{formData.name}</p>
        </div>
      )}

      {/* Visibility Options */}
      <div className="space-y-4">
        {/* Private Option */}
        <button
          type="button"
          onClick={() => updateFormData({ visibility: 'private' })}
          className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
            visibility === 'private'
              ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              visibility === 'private'
                ? 'bg-indigo-100'
                : 'bg-gray-100'
            }`}>
              <Lock className={`w-6 h-6 ${
                visibility === 'private'
                  ? 'text-indigo-600'
                  : 'text-gray-500'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`font-semibold text-lg ${
                  visibility === 'private'
                    ? 'text-indigo-900'
                    : 'text-gray-900'
                }`}>
                  Privat
                </span>
                {visibility === 'private' && (
                  <span className="px-2 py-0.5 bg-indigo-500 text-white text-xs font-medium rounded-full">
                    Gewaehlt
                  </span>
                )}
              </div>
              <p className={`text-sm ${
                visibility === 'private'
                  ? 'text-indigo-700'
                  : 'text-gray-600'
              }`}>
                Nur du und Personen, mit denen du den Ort teilst, koennen ihn sehen.
              </p>

              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Eye className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700">Sofort sichtbar fuer dich</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-700">Mit Freunden teilbar</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Shield className="w-4 h-4 text-purple-500" />
                  <span className="text-gray-700">Keine Moderation noetig</span>
                </div>
              </div>
            </div>
          </div>
        </button>

        {/* Public Option */}
        <button
          type="button"
          onClick={() => updateFormData({ visibility: 'public' })}
          className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
            visibility === 'public'
              ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              visibility === 'public'
                ? 'bg-green-100'
                : 'bg-gray-100'
            }`}>
              <Globe className={`w-6 h-6 ${
                visibility === 'public'
                  ? 'text-green-600'
                  : 'text-gray-500'
              }`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`font-semibold text-lg ${
                  visibility === 'public'
                    ? 'text-green-900'
                    : 'text-gray-900'
                }`}>
                  Oeffentlich
                </span>
                {visibility === 'public' && (
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
                    Gewaehlt
                  </span>
                )}
              </div>
              <p className={`text-sm ${
                visibility === 'public'
                  ? 'text-green-700'
                  : 'text-gray-600'
              }`}>
                Alle GoodPlace-Nutzer koennen deinen Ort nach Freigabe entdecken.
              </p>

              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="w-4 h-4 text-green-500" />
                  <span className="text-gray-700">Fuer alle sichtbar nach Freigabe</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-gray-700">Wird von einem Admin geprueft</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-700">Community kann Bewertungen abgeben</span>
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Info Box */}
      <div className={`p-4 rounded-xl ${
        visibility === 'private'
          ? 'bg-indigo-50 border border-indigo-200'
          : 'bg-green-50 border border-green-200'
      }`}>
        <p className={`text-sm ${
          visibility === 'private'
            ? 'text-indigo-800'
            : 'text-green-800'
        }`}>
          {visibility === 'private' ? (
            <>
              <strong>Hinweis:</strong> Du kannst einen privaten Ort jederzeit oeffentlich machen.
              Oeffentliche Orte koennen jedoch nicht wieder privat werden.
            </>
          ) : (
            <>
              <strong>Hinweis:</strong> Dein Ort wird nach dem Absenden von einem Moderator geprueft.
              Du erhaeltst eine Benachrichtigung, sobald er freigegeben wurde.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

export default StepVisibility;
